import React, { useState, useEffect } from 'react';
import { Plus, X, Link as LinkIcon } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useEventManagement, TrackImage } from '../../hooks/useEventManagement';
import { supabase } from '../../lib/supabase';

// Components
import EventImageGrid from '../../components/events/EventImageGrid';
import UploadModal from '../../components/events/UploadModal';
import EventDatesModal from '../../components/events/EventDatesModal';
import EventModal from '../../components/events/EventModal';
import TicketAllocationSettings from './TicketAllocationSettings';
import TicketShopUrlModal from '../../components/events/TicketShopUrlModal';

export default function EventManagement() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const {
    trackImages,
    events,
    globalCategories,
    loading,
    fetchTrackImages,
    fetchEvents,
    fetchGlobalCategories,
    fetchExistingEventCategories,
    handleDeleteTrackImage,
    formatDateForDatabase,
    formatDateForDisplay,
    hasExistingEvent
  } = useEventManagement();

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showEventDatesModal, setShowEventDatesModal] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showTicketSettingsModal, setShowTicketSettingsModal] = useState(false);
  const [showTicketShopUrlModal, setShowTicketShopUrlModal] = useState(false);
  const [selectedTrackImage, setSelectedTrackImage] = useState<TrackImage | null>(null);
  const [eventDates, setEventDates] = useState<string[]>(['']);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [applyToAllDates, setApplyToAllDates] = useState(true);
  const [selectedEventDate, setSelectedEventDate] = useState<string>('');
  const [selectedVehicleFilter, setSelectedVehicleFilter] = useState<'car' | 'bike'>('car');
  const [selectedEvent, setSelectedEvent] = useState<string | null>(null);
  const [ticketShopUrl, setTicketShopUrl] = useState('');
  
  // Add state for title and description editing
  const [editingTitle, setEditingTitle] = useState('');
  const [editingDescription, setEditingDescription] = useState('');
  
  // Add state for drift event settings
  const [driftEventSettings, setDriftEventSettings] = useState({
    registration_fee: 100
  });

  const openEventDatesModal = (image: TrackImage) => {
    setSelectedTrackImage(image);
    const existingDates = image.event_dates?.map(ed => ed.event_date) || [''];
    setEventDates(existingDates.length > 0 ? existingDates : ['']);
    
    // Set the editing fields with current values
    setEditingTitle(image.title);
    setEditingDescription(image.description);
    
    setShowEventDatesModal(true);
  };

  const openEventModal = async (image: TrackImage) => {
    setSelectedTrackImage(image);
    
    // Auto-populate available event dates
    const availableDates = image.event_dates?.map(ed => ed.event_date) || [];
    
    // Set default event date to first available date
    const defaultEventDate = availableDates.length > 0 ? availableDates[0] : '';
    
    setSelectedEventDate(defaultEventDate);
    setSelectedVehicleFilter('car'); // Reset to car filter
    setApplyToAllDates(availableDates.length > 1); // Default to true if multiple dates
    
    // For drift events, reset drift settings to defaults
    if (image.track_type === 'drift') {
      setDriftEventSettings({
        registration_fee: 100
      });
    } else {
      // Fetch and set existing categories for drag events
      const existingCategoryIds = await fetchExistingEventCategories(image.id);
      setSelectedCategories(existingCategoryIds);
    }
    
    setShowEventModal(true);
  };

  const openTicketSettingsModal = (eventId: string) => {
    setSelectedEvent(eventId);
    setShowTicketSettingsModal(true);
  };

  const openTicketShopUrlModal = (eventId: string) => {
    setSelectedEvent(eventId);
    
    // Find the current event to get its ticket shop URL
    const event = events.find(e => e.id === eventId);
    if (event) {
      setTicketShopUrl(event.ticket_shop_url || '');
      
      // Find the track image for this event
      const trackImage = trackImages.find(img => img.id === event.track_image_id);
      if (trackImage) {
        setSelectedTrackImage(trackImage);
        setApplyToAllDates(trackImage.event_dates && trackImage.event_dates.length > 1);
      }
    } else {
      setTicketShopUrl('');
    }
    
    setShowTicketShopUrlModal(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-white">Event Management</h1>
          <div className="flex gap-3">
            <button
              onClick={() => setShowUploadModal(true)}
              className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
            >
              <Plus className="h-5 w-5" />
              Add Event
            </button>
          </div>
        </div>

        {/* Image Grid */}
        <EventImageGrid 
          trackImages={trackImages}
          openEventDatesModal={openEventDatesModal}
          openEventModal={openEventModal}
          handleDeleteTrackImage={handleDeleteTrackImage}
          hasExistingEvent={hasExistingEvent}
          formatDateForDisplay={formatDateForDisplay}
          openTicketSettingsModal={openTicketSettingsModal}
          openTicketShopUrlModal={openTicketShopUrlModal}
        />

        {/* Upload Modal */}
        <UploadModal 
          showUploadModal={showUploadModal}
          setShowUploadModal={setShowUploadModal}
          fetchTrackImages={fetchTrackImages}
        />

        {/* Event Dates Modal */}
        <EventDatesModal 
          selectedTrackImage={selectedTrackImage}
          setSelectedTrackImage={setSelectedTrackImage}
          showEventDatesModal={showEventDatesModal}
          setShowEventDatesModal={setShowEventDatesModal}
          eventDates={eventDates}
          setEventDates={setEventDates}
          editingTitle={editingTitle}
          setEditingTitle={setEditingTitle}
          editingDescription={editingDescription}
          setEditingDescription={setEditingDescription}
          fetchTrackImages={fetchTrackImages}
          formatDateForDatabase={formatDateForDatabase}
        />

        {/* Create Event Modal */}
        <EventModal 
          selectedTrackImage={selectedTrackImage}
          setSelectedTrackImage={setSelectedTrackImage}
          showEventModal={showEventModal}
          setShowEventModal={setShowEventModal}
          selectedEventDate={selectedEventDate}
          setSelectedEventDate={setSelectedEventDate}
          selectedCategories={selectedCategories}
          setSelectedCategories={setSelectedCategories}
          selectedVehicleFilter={selectedVehicleFilter}
          setSelectedVehicleFilter={setSelectedVehicleFilter}
          applyToAllDates={applyToAllDates}
          setApplyToAllDates={setApplyToAllDates}
          driftEventSettings={driftEventSettings}
          setDriftEventSettings={setDriftEventSettings}
          globalCategories={globalCategories}
          events={events}
          fetchEvents={fetchEvents}
          fetchTrackImages={fetchTrackImages}
          formatDateForDisplay={formatDateForDisplay}
          fetchExistingEventCategories={fetchExistingEventCategories}
          ticketShopUrl={ticketShopUrl}
          setTicketShopUrl={setTicketShopUrl}
        />

        {/* Ticket Settings Modal */}
        {showTicketSettingsModal && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
            <div className="bg-zinc-900 rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">
                  Event Ticket Settings
                </h2>
                <button 
                  onClick={() => setShowTicketSettingsModal(false)}
                  className="text-gray-400 hover:text-white"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              <TicketAllocationSettings eventId={selectedEvent || undefined} />
              
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowTicketSettingsModal(false)}
                  className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-lg font-semibold"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Ticket Shop URL Modal */}
        <TicketShopUrlModal
          showModal={showTicketShopUrlModal}
          onClose={() => setShowTicketShopUrlModal(false)}
          selectedEvent={selectedEvent}
          selectedTrackImage={selectedTrackImage}
          ticketShopUrl={ticketShopUrl}
          setTicketShopUrl={setTicketShopUrl}
          applyToAllDates={applyToAllDates}
          setApplyToAllDates={setApplyToAllDates}
          fetchEvents={fetchEvents}
          events={events}
        />
      </div>
    </div>
  );
}